import React, { useState, useEffect, useMemo } from 'react'
import { Edit, Trash2, Search, RefreshCw, Plus } from 'lucide-react'
import { FlowEditorWorkspace } from './FlowEditorWorkspace'
import { flowConfigService } from '../services/flowConfigService'
import { FlowConfig, FlowConfigData, FlowCredential, CreateFlowCredentialRequest } from '../types/flowConfig.types'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

interface FlowConfigManagerProps {
  onClose: () => void
  onEditingChange?: (isEditing: boolean) => void
}

const FlowConfigManager: React.FC<FlowConfigManagerProps> = ({ onEditingChange }) => {
  const [flowConfigs, setFlowConfigs] = useState<FlowConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedFlow, setSelectedFlow] = useState<FlowConfig | null>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [flowToDelete, setFlowToDelete] = useState<FlowConfig | null>(null)
  const [credentials, setCredentials] = useState<FlowCredential[]>([])
  const [showCredentialModal, setShowCredentialModal] = useState(false)
  const [editingCredential, setEditingCredential] = useState<FlowCredential | null>(null)
  const [credentialForm, setCredentialForm] = useState<CreateFlowCredentialRequest>({
    name: '',
    credential_key: '',
    base_url: '',
    client_id: '',
    client_secret: '',
    auth_type: 'basic',
    is_active: true
  })

  useEffect(() => {
    loadFlowConfigs()
    loadCredentials()
    
    // Listener para o evento de criar fluxo do header
    const handleCreateFlowEvent = () => {
      handleCreateFlow()
    }
    
    window.addEventListener('createFlow', handleCreateFlowEvent)
    
    return () => {
      window.removeEventListener('createFlow', handleCreateFlowEvent)
    }
  }, [])

  const loadFlowConfigs = async () => {
    try {
      setLoading(true)
      const response = await flowConfigService.getFlowConfigs()
      setFlowConfigs(response.data.data.flow_configs)
    } catch (error) {
      logger.error('Erro ao carregar fluxos:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadCredentials = async () => {
    try {
      const response = await flowConfigService.getCredentials()
      setCredentials(response.data.data || [])
    } catch (error) {
      logger.error('Erro ao carregar credenciais:', error)
      toast.error('Erro ao carregar credenciais externas')
    }
  }

  const handleCreateCredential = () => {
    setEditingCredential(null)
    setCredentialForm({
      name: '',
      credential_key: '',
      base_url: '',
      client_id: '',
      client_secret: '',
      auth_type: 'basic',
      is_active: true
    })
    setShowCredentialModal(true)
  }

  const handleEditCredential = (cred: FlowCredential) => {
    setEditingCredential(cred)
    setCredentialForm({
      name: cred.name,
      credential_key: cred.credential_key,
      base_url: cred.base_url,
      client_id: cred.client_id,
      client_secret: '',
      auth_type: cred.auth_type,
      is_active: cred.is_active
    })
    setShowCredentialModal(true)
  }

  const handleSaveCredential = async () => {
    try {
      if (!credentialForm.name || !credentialForm.credential_key || !credentialForm.base_url || !credentialForm.client_id) {
        toast.error('Preencha os campos obrigatórios da credencial')
        return
      }
      if (!editingCredential && !credentialForm.client_secret) {
        toast.error('Client secret é obrigatório ao criar credencial')
        return
      }
      if (editingCredential) {
        await flowConfigService.updateCredential(editingCredential.id, credentialForm)
        toast.success('Credencial atualizada com sucesso')
      } else {
        await flowConfigService.createCredential(credentialForm)
        toast.success('Credencial criada com sucesso')
      }
      setShowCredentialModal(false)
      setEditingCredential(null)
      await loadCredentials()
    } catch (error) {
      logger.error('Erro ao salvar credencial:', error)
      toast.error('Erro ao salvar credencial externa')
    }
  }

  const handleDeleteCredential = async (cred: FlowCredential) => {
    try {
      await flowConfigService.deleteCredential(cred.id)
      toast.success('Credencial removida com sucesso')
      await loadCredentials()
    } catch (error) {
      logger.error('Erro ao remover credencial:', error)
      toast.error('Erro ao remover credencial')
    }
  }

  const handleCreateFlow = () => {
    setSelectedFlow(null)
    setIsCreating(true)
    setShowEditor(true)
    onEditingChange?.(true)
  }

  const handleEditFlow = (flow: FlowConfig) => {
    setSelectedFlow(flow)
    setIsCreating(false)
    setShowEditor(true)
    onEditingChange?.(true)
  }

  const handleDeleteFlow = async () => {
    if (!flowToDelete) return

    try {
      await flowConfigService.deleteFlowConfig(flowToDelete.id)
      toast.success('Fluxo deletado com sucesso!')
      setShowDeleteModal(false)
      setFlowToDelete(null)
      await loadFlowConfigs()
    } catch (error) {
      logger.error('Erro ao deletar fluxo:', error)
      toast.error('Erro ao deletar fluxo')
    }
  }

  const handleDeleteClick = (flow: FlowConfig) => {
    setFlowToDelete(flow)
    setShowDeleteModal(true)
  }

  const handleSaveFlow = async (flowData: any) => {
    logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: Iniciando salvamento...')
    logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: isCreating:', isCreating)
    logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: flowData:', flowData)
    logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: selectedFlow:', selectedFlow)
    
    try {
      if (isCreating) {
        logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: Criando novo fluxo...')
        
        // Para criação, montar o objeto completo
        const flowToCreate = {
          name: flowData.name,
          description: flowData.description,
          flow_config: {
            steps: flowData.steps,
            start_step: flowData.start_step,
            end_message: flowData.end_message,
            variables: flowData.variables,
            ...(flowData.ui_layout !== undefined && flowData.ui_layout !== null
              ? { ui_layout: flowData.ui_layout }
              : {})
          },
          is_active: true
        }
        
        logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: Dados para criação:', flowToCreate)
        await flowConfigService.createFlowConfig(flowToCreate)
        logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: Novo fluxo criado com sucesso')
        toast.success('Fluxo criado com sucesso!')
      } else {
        // Para edição, montar o objeto completo com name, description e flow_config
        if (!selectedFlow?.id) {
          logger.error('🔍 DEBUG FlowConfigManager handleSaveFlow: selectedFlow.id não encontrado')
          return
        }
        
        const flowToUpdate = {
          id: selectedFlow.id,
          name: flowData.name || selectedFlow.name, // Usar name do flowData se disponível
          description: flowData.description || selectedFlow.description, // Usar description do flowData se disponível
          flow_config: flowData, // flowData contém { steps, start_step, ... }
          is_active: selectedFlow.is_active
        }
        logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: Atualizando fluxo:', flowToUpdate)
        logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: flow_config:', JSON.stringify(flowToUpdate.flow_config, null, 2))
        await flowConfigService.updateFlowConfig(flowToUpdate)
        logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: Fluxo atualizado com sucesso')
        toast.success('Fluxo atualizado com sucesso!')
      }
      await loadFlowConfigs()
      setShowEditor(false)
      setSelectedFlow(null)
      setIsCreating(false)
      onEditingChange?.(false)
      logger.debug('🔍 DEBUG FlowConfigManager handleSaveFlow: Salvamento concluído com sucesso')
    } catch (error) {
      logger.error('🔍 DEBUG FlowConfigManager handleSaveFlow: Erro ao salvar fluxo:', error)
      toast.error('Erro ao salvar fluxo')
    }
  }

  const handleCloseEditor = () => {
    setShowEditor(false)
    setSelectedFlow(null)
    setIsCreating(false)
    onEditingChange?.(false)
  }

  const filteredFlowConfigs = flowConfigs.filter(flow => {
    const matchesSearch = flow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         flow.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterStatus === 'all' || 
                          (filterStatus === 'active' && flow.is_active) ||
                          (filterStatus === 'inactive' && !flow.is_active)
    return matchesSearch && matchesFilter
  })

  const convertFlowConfigForEditor = (flow: FlowConfig): FlowConfigData => {
    logger.debug('🔍 DEBUG convertFlowConfigForEditor: Convertendo flow:', flow)
    try {
      if (typeof flow.flow_config === 'string') {
        const parsed = JSON.parse(flow.flow_config)
        logger.debug('🔍 DEBUG convertFlowConfigForEditor: Flow config parseado:', parsed)
        return parsed
      }
      logger.debug('🔍 DEBUG convertFlowConfigForEditor: Flow config já é objeto:', flow.flow_config)
      return flow.flow_config
    } catch (error) {
      logger.error('🔍 DEBUG convertFlowConfigForEditor: Erro ao converter configuração do fluxo:', error)
      return {
        steps: {},
        start_step: 'initial'
      }
    }
  }

  const editorSessionKey = useMemo(
    () => (isCreating ? 'new' : String(selectedFlow?.id ?? 'unknown')),
    [isCreating, selectedFlow?.id]
  )

  const flowConfigDataForEditor = useMemo((): FlowConfigData => {
    const base = selectedFlow
      ? convertFlowConfigForEditor(selectedFlow)
      : {
          name: '',
          description: '',
          steps: {},
          start_step: 'initial'
        }
    if (selectedFlow && !isCreating) {
      return {
        ...base,
        name: selectedFlow.name,
        description: selectedFlow.description
      }
    }
    return base
  }, [selectedFlow, isCreating])

  if (showEditor) {
    logger.debug('🔍 DEBUG FlowConfigManager: Enviando flowConfigData para FlowEditorWorkspace:', flowConfigDataForEditor)
    logger.debug('🔍 DEBUG FlowConfigManager: isCreating:', isCreating)
    logger.debug('🔍 DEBUG FlowConfigManager: selectedFlow:', selectedFlow)

    return (
      <FlowEditorWorkspace
        key={editorSessionKey}
        sessionKey={editorSessionKey}
        initialFlowConfig={flowConfigDataForEditor}
        onSave={handleSaveFlow}
        onCancel={handleCloseEditor}
        isEditing={!isCreating}
      />
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Credenciais externas */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Credenciais Externas de Fluxo</h3>
            <p className="text-sm text-gray-600">Configure `base_url`, `client_id` e `client_secret`.</p>
          </div>
          <button
            onClick={handleCreateCredential}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Nova Credencial
          </button>
        </div>
        {credentials.length === 0 ? (
          <p className="text-sm text-gray-500">Nenhuma credencial cadastrada.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {credentials.map((cred) => (
              <div key={cred.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{cred.name} <span className="text-gray-500">({cred.credential_key})</span></p>
                  <p className="text-xs text-gray-600">{cred.base_url} - {cred.client_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEditCredential(cred)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                    title="Editar credencial"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => void handleDeleteCredential(cred)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md"
                    title="Remover credencial"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar fluxos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
          </div>
          <button
            onClick={loadFlowConfigs}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>


      {/* Flow Configs List */}
      <div className="bg-white rounded-lg shadow-sm border">
        {filteredFlowConfigs.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || filterStatus !== 'all' 
                ? 'Nenhum fluxo encontrado'
                : 'Nenhum fluxo encontrado'
              }
            </h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || filterStatus !== 'all' 
                ? 'Tente ajustar os filtros de busca'
                : 'Comece criando seu primeiro fluxo de conversa'
              }
            </p>
            <button
              onClick={handleCreateFlow}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Criar Primeiro Fluxo
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredFlowConfigs.map((flow) => (
              <div key={flow.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">{flow.name}</h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        flow.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {flow.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-1">{flow.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span>Criado em: {new Date(flow.created_at).toLocaleDateString('pt-BR')}</span>
                      <span>Atualizado em: {new Date(flow.updated_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditFlow(flow)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar fluxo"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(flow)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Excluir fluxo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && flowToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Confirmar Exclusão
              </h3>
              <p className="text-gray-600 mb-6">
                Tem certeza que deseja excluir o fluxo "{flowToDelete.name}"? 
                Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setFlowToDelete(null)
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteFlow}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de credencial */}
      {showCredentialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingCredential ? 'Editar Credencial' : 'Nova Credencial'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  value={credentialForm.name}
                  onChange={(e) => setCredentialForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Nome"
                />
                <input
                  value={credentialForm.credential_key}
                  onChange={(e) => setCredentialForm((prev) => ({ ...prev, credential_key: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="credential_key"
                />
                <input
                  value={credentialForm.base_url}
                  onChange={(e) => setCredentialForm((prev) => ({ ...prev, base_url: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md md:col-span-2"
                  placeholder="https://provedor.exemplo.com"
                />
                <input
                  value={credentialForm.client_id}
                  onChange={(e) => setCredentialForm((prev) => ({ ...prev, client_id: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Client ID"
                />
                <input
                  type="password"
                  value={credentialForm.client_secret}
                  onChange={(e) => setCredentialForm((prev) => ({ ...prev, client_secret: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                  placeholder={editingCredential ? 'Client Secret (opcional para manter)' : 'Client Secret'}
                />
                <select
                  value={credentialForm.auth_type || 'basic'}
                  onChange={(e) => setCredentialForm((prev) => ({ ...prev, auth_type: e.target.value as any }))}
                  className="px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="basic">basic</option>
                  <option value="bearer">bearer</option>
                  <option value="custom_headers">custom_headers</option>
                  <option value="none">none</option>
                </select>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={credentialForm.is_active !== false}
                    onChange={(e) => setCredentialForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                  Ativa
                </label>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCredentialModal(false)
                    setEditingCredential(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => void handleSaveCredential()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FlowConfigManager